'use client';

/* ──────────────────────────────────────────────
 *  RecapCard — récap en carte repliable (spec Parcours § 8) :
 *  en largeur Regular (560-899 px) en tête de chaque étape, en
 *  Compact en tête de la finalisation seulement (ailleurs, la feuille
 *  de la barre du bas). Repliée, une ligne « 2 prestations · 55 min ·
 *  53 € ▾ » ; dépliée, le même contenu que la feuille et la sidebar.
 *  Visibilité décidée en CSS par le conteneur `bt`.
 * ────────────────────────────────────────────── */

import { type JSX, useId, useState } from 'react';
import { useBooking } from '../context/BookingContext';
import { useBookingCodes } from '../context/BookingCodesContext';
import { computeSummaryStruckTotal, computeSummaryTotalValue } from '../utils/summary-total';
import { BookingSummary } from './BookingSummary';

export function RecapCard(): JSX.Element | null {
  const { ctx, labels, currentStep } = useBooking();
  const { amounts, summaryError } = useBookingCodes();
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const count = ctx.cartItems.length;

  if (count === 0 || currentStep === 'success' || currentStep === 'quote' || currentStep === 'loading') return null;

  // Résumé du panier en échec : ni total ni durée périmés sur la ligne repliée, le contenu propose « Réessayer ».
  const total = computeSummaryTotalValue(ctx.cartSummary, ctx.cartSummaryLoading, labels, amounts, summaryError);
  const struck = computeSummaryStruckTotal(amounts, summaryError);
  const parts = [
    `${count} ${count > 1 ? labels.prestations_plural : labels.prestation_singular}`,
    summaryError ? undefined : ctx.cartSummary?.total_duration,
  ].filter(Boolean);

  return (
    <section className={`bt-recap-card ${currentStep === 'checkout' ? 'bt-recap-card--checkout' : ''}`}>
      <button
        type="button"
        className="bt-recap-card__toggle"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={(): void => setOpen((value): boolean => !value)}
      >
        <span className="bt-recap-card__title">
          {ctx.entryMode === 'multi' ? labels.selection_title : labels.summary_title_single}
        </span>
        <span className="bt-recap-card__line">
          <span>{parts.join(' · ')}</span>
          <span className="bt-recap-card__total">
            {struck ? <s className="bt-recap-card__struck">{struck}</s> : null}
            {total}
          </span>
          <span className="bt-recap-card__chevron" aria-hidden="true">
            {open ? '▴' : '▾'}
          </span>
        </span>
      </button>
      {open ? (
        <div className="bt-recap-card__content" id={contentId}>
          {/* Finalisation : le champ code est déjà dans le formulaire, pas deux champs à l'écran. */}
          <BookingSummary variant="sheet" withoutCode={currentStep === 'checkout'} />
        </div>
      ) : null}
    </section>
  );
}
